import { PermissionAssignmentName } from '@acre/graphql/constants'

type getUserOrganisationIdsProps = {
  user: User
}

type getUserOrganisationIdsReturnType = {
  userOrganisationId: string
  firstReviewingForHint: string
  all: string[]
}

export const getUserOrganisationIds = ({ user }: getUserOrganisationIdsProps): getUserOrganisationIdsReturnType => {
  let organisationIds: getUserOrganisationIdsReturnType = {
    userOrganisationId: '',
    firstReviewingForHint: '',
    all: [],
  }
  if (!user) return organisationIds
  const { organisation_id, organisation } = user

  if (organisation_id) {
    organisationIds.all.push(organisation_id)
    organisationIds.userOrganisationId = organisation_id
  }

  if (!organisation) return organisationIds

  const { metadata } = organisation
  if (Array.isArray(metadata) && metadata.length) {
    metadata.forEach((datum) => {
      if (datum && datum.name === 'reviewing_for_hint' && datum.string_value) {
        organisationIds.all.push(datum.string_value)
        organisationIds.firstReviewingForHint = !organisationIds.firstReviewingForHint ? datum.string_value : ''
      }
    })
  }

  return organisationIds
}

export const GATE_TO_PERMISSION: Partial<Record<PermissionGateName, PermissionAssignmentName>> = {
  ACCOUNTING: PermissionAssignmentName.ACCOUNTING,
  ACRE_CASE_IMPORT: PermissionAssignmentName.ACRE_CASE_IMPORT,
  ACRE_ORGANISATION_CREATE_REG_ORG: PermissionAssignmentName.ACRE_ORGANISATION_CREATE_REG_ORG,
  APPLY_PAYMENT_LEDGER: PermissionAssignmentName.APPLY_PAYMENT_LEDGER,
  CASE_CHANGE_OWNER: PermissionAssignmentName.CASE_CHANGE_OWNER,
  CLIENT_VIEW_PROTECTED: PermissionAssignmentName.CLIENT_VIEW_PROTECTED,
  CREATE_CASE_LEDGER: PermissionAssignmentName.CREATE_CASE_LEDGER,
  DELETE_LEDGER: PermissionAssignmentName.DELETE_LEDGER,
  EDIT_CASE: PermissionAssignmentName.CASE_EDIT_CASE,
  EDIT_CASE_LEDGER: PermissionAssignmentName.EDIT_CASE_LEDGER,
  EDIT_CLIENT: PermissionAssignmentName.CLIENT_EDIT_CLIENT,
  EDIT_CLIENT_RESTRICTED: PermissionAssignmentName.CLIENT_EDIT_CLIENT_RESTRICTED,
  EDIT_GENERAL_INSURANCE: PermissionAssignmentName.REGULATED_GENERALINSURANCE,
  EDIT_GENERAL_INSURANCE_PROVISIONAL: PermissionAssignmentName.REGULATED_GENERALINSURANCE_PROVISIONAL,
  EDIT_ORGANISATION_COMPANY_INFORMATION_COMPANY_SETTINGS: PermissionAssignmentName.ORGANISATION_EDIT_ORG,
  EDIT_ORGANISATION_GENERAL_INSURANCE_COMPANY_SETTINGS: PermissionAssignmentName.ORGANISATION_EDIT_METADATA,
  EDIT_ORGANISATION_MORTGAGE_FEES_COMPANY_SETTINGS: PermissionAssignmentName.ORGANISATION_EDIT_FEES,
  EDIT_PROVIDER_STATEMENT: PermissionAssignmentName.EDIT_PROVIDER_STATEMENT,
  EDIT_USER_STATUS_COMPANY_SETTINGS: PermissionAssignmentName.USER_EDIT_USER,
  EDIT_UNLINKED_PAYMENTS_LEDGER: PermissionAssignmentName.EDIT_UNLINKED_PAYMENTS_LEDGER,
  FIRM_MANAGETEMPLATES: PermissionAssignmentName.FIRM_MANAGETEMPLATES,
  GROUP_EDIT_GROUP: PermissionAssignmentName.GROUP_EDIT_GROUP,
  GROUP_VIEW_GROUP: PermissionAssignmentName.GROUP_VIEW_GROUP,
  MANAGE_ORGANISATION: PermissionAssignmentName.MANAGE_ORGANISATION,
  ORGANISATION_CREATE_AR_ORG: PermissionAssignmentName.ORGANISATION_CREATE_AR_ORG,
  ORGANISATION_CREATE_INTRODUCER_ORG: PermissionAssignmentName.ORGANISATION_CREATE_INTRODUCER_ORG,
  ORGANISATION_EDIT_METADATA: PermissionAssignmentName.ORGANISATION_EDIT_METADATA,
  ORGANISATION_EDIT_ORG: PermissionAssignmentName.ORGANISATION_EDIT_ORG,
  ORGANISATION_EDIT_ORG_FINANCIAL_CONFIGURATION: PermissionAssignmentName.ORGANISATION_EDIT_ORG_FINANCIAL_CONFIGURATION,
  ORGANISATION_EDIT_PROTECTED: PermissionAssignmentName.ORGANISATION_EDIT_PROTECTED,
  ORGANISATION_EDIT_SYSTEM_METADATA: PermissionAssignmentName.ORGANISATION_EDIT_SYSTEM_METADATA,
  ORGANISATION_REPORTING: PermissionAssignmentName.ORGANISATION_REPORTING,
  ORGANISATION_VIEW: PermissionAssignmentName.ORGANISATION_VIEW,
  ORGANISATION_VIEW_ORG_FINANCIAL_CONFIGURATION: PermissionAssignmentName.ORGANISATION_VIEW_ORG_FINANCIAL_CONFIGURATION,
  PERMISSIONS_EDIT_PERMISSION: PermissionAssignmentName.PERMISSIONS_EDIT_PERMISSION,
  PERMISSIONS_EDIT_REGULATED_PERMISSION: PermissionAssignmentName.PERMISSIONS_EDIT_REGULATED_PERMISSION,
  PERMISSIONS_VIEW_PERMISSIONS: PermissionAssignmentName.PERMISSIONS_VIEW_PERMISSIONS,
  REFERRAL_PARTNERS: PermissionAssignmentName.REFERRAL_PARTNERS,
  REGULATED_COMPLIANCE_ADDCASENOTES: PermissionAssignmentName.REGULATED_COMPLIANCE_ADDCASENOTES,
  REGULATED_COMPLIANCE_APPROVEADVICE: PermissionAssignmentName.REGULATED_COMPLIANCE_APPROVEADVICE,
  REGULATED_COMPLIANCE_APPROVEAML: PermissionAssignmentName.REGULATED_COMPLIANCE_APPROVEAML,
  REGULATED_COMPLIANCE_APPROVECASES: PermissionAssignmentName.REGULATED_COMPLIANCE_APPROVECASES,
  REGULATED_COMPLIANCE_APPROVEEIDVOVERRIDE: PermissionAssignmentName.REGULATED_COMPLIANCE_APPROVEEIDVOVERRIDE,
  REGULATED_COMPLIANCE_VIEWCASEDETAIL: PermissionAssignmentName.REGULATED_COMPLIANCE_VIEWCASEDETAIL,
  REGULATED_COMPLIANCE_VIEWCASENOTES: PermissionAssignmentName.REGULATED_COMPLIANCE_VIEWCASENOTES,
  REGULATED_DEBTCONSOLIDATION: PermissionAssignmentName.REGULATED_DEBTCONSOLIDATION,
  REGULATED_EQUITYRELEASE: PermissionAssignmentName.REGULATED_EQUITYRELEASE,
  REGULATED_EQUITYRELEASE_PROVISIONAL: PermissionAssignmentName.REGULATED_EQUITYRELEASE_PROVISIONAL,
  REGULATED_GENERALINSURANCE: PermissionAssignmentName.REGULATED_GENERALINSURANCE,
  REGULATED_GENERALINSURANCE_PROVISIONAL: PermissionAssignmentName.REGULATED_GENERALINSURANCE_PROVISIONAL,
  REGULATED_MORTGAGE: PermissionAssignmentName.REGULATED_MORTGAGE,
  REGULATED_MORTGAGE_PROVISIONAL: PermissionAssignmentName.REGULATED_MORTGAGE_PROVISIONAL,
  REGULATED_PROTECTIONINSURANCE: PermissionAssignmentName.REGULATED_PROTECTIONINSURANCE,
  REGULATED_PROTECTIONINSURANCE_PROVISIONAL: PermissionAssignmentName.REGULATED_PROTECTIONINSURANCE_PROVISIONAL,
  USER_CREATE_USER: PermissionAssignmentName.USER_CREATE_USER,
  USER_EDIT_AUTHENTICATION: PermissionAssignmentName.USER_EDIT_AUTHENTICATION,
  USER_EDIT_USER: PermissionAssignmentName.USER_EDIT_USER,
  USER_EDIT_USER_METADATA: PermissionAssignmentName.USER_EDIT_USER_METADATA,
  VIEW_BROKER_DASHBOARD: PermissionAssignmentName.CASE_VIEW_CASE,
  VIEW_CASE_COMPLIANCE: PermissionAssignmentName.REGULATED_COMPLIANCE_VIEWCASEDETAIL,
  VIEW_CASE_FLAGS: PermissionAssignmentName.REGULATED_COMPLIANCE_VIEWCASENOTES,
  VIEW_CASE_SECTION: PermissionAssignmentName.CASE_VIEW_CASE,
  VIEW_CLIENT_SECTION: PermissionAssignmentName.CLIENT_VIEW_CLIENT,
  VIEW_MI_DASHBOARD: PermissionAssignmentName.REGULATED_COMPLIANCE_VIEWCOMPLIANCEDASHBOARD,
  VIEW_OVERRIDE_CLIENT_EIDV: PermissionAssignmentName.REGULATED_COMPLIANCE_APPROVEEIDVOVERRIDE,
  VIEW_PROVIDER_STATEMENT: PermissionAssignmentName.VIEW_PROVIDER_STATEMENT,
  VIEW_UNLINKED_PAYMENTS_LEDGER: PermissionAssignmentName.VIEW_UNLINKED_PAYMENTS_LEDGER,
  WRITE_OFF_LEDGER: PermissionAssignmentName.WRITE_OFF_LEDGER,
}
